'use client';

import { useState, useRef, useEffect } from "react";
import Script from "next/script";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Play, ArrowRight, CheckCircle2,
  Users, BarChart3, Menu, X,
  Rocket, TrendingUp, Leaf, Scissors,
  Brain, Target, Clapperboard, PackageCheck, Eye, BadgeCheck,
  Instagram as InstagramIcon, Youtube, Music2, Infinity as InfinityIcon,
  Package, Sparkles, Star, Quote, GraduationCap, ArrowLeftRight,
  Zap, ShoppingBag, Sun, Mic, Clock3, Gift, ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import VideoShowcase from "@/components/marketing/VideoShowcase";
import HeroVideoCarousel from "@/components/marketing/HeroVideoCarousel";
import { Reveal } from "@/components/marketing/Reveal";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    _paq?: { push: (args: unknown[]) => void };
  }
}

type LeadFormState = {
  name: string;
  email: string;
  company: string;
  phone: string;
  budget: string;
  sector: string;
  timing: string;
  message: string;
  rgpd: boolean;
};

type TrackingParamKey = "gclid" | "gbraid" | "wbraid" | "utm_campaign" | "utm_term";
type TrackingParams = Partial<Record<TrackingParamKey, string>>;

const TRACKING_PARAM_KEYS: TrackingParamKey[] = ["gclid", "gbraid", "wbraid", "utm_campaign", "utm_term"];
const TRACKING_STORAGE_PREFIX = "ugcookie_tracking_";
const GOOGLE_TAG_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_ID;
const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
const GOOGLE_ADS_CONVERSION_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID;
const GOOGLE_ADS_LEAD_SEND_TO = process.env.NEXT_PUBLIC_GOOGLE_ADS_LEAD_SEND_TO;
const MATOMO_URL = process.env.NEXT_PUBLIC_MATOMO_URL;
const MATOMO_SITE_ID = process.env.NEXT_PUBLIC_MATOMO_SITE_ID;

function getLeadValueFromBudget(budget: string): number {
  if (budget.includes("Plus de 20")) return 600;
  if (budget.includes("10 000")) return 350;
  if (budget.includes("5 000")) return 180;
  if (budget.includes("2 000")) return 80;
  return 20;
}

function persistTrackingParamsFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const maxAge = 90 * 24 * 60 * 60;

  TRACKING_PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (!value) return;
    window.localStorage.setItem(`${TRACKING_STORAGE_PREFIX}${key}`, value);
    document.cookie = `${key}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure`;
  });
}

function getStoredTrackingParams(): TrackingParams {
  if (typeof window === "undefined") return {};
  return TRACKING_PARAM_KEYS.reduce<TrackingParams>((acc, key) => {
    const value = window.localStorage.getItem(`${TRACKING_STORAGE_PREFIX}${key}`);
    if (value) acc[key] = value;
    return acc;
  }, {});
}

function buildGoogleTagInitScript(): string {
  const primaryTagId = GOOGLE_TAG_ID || GOOGLE_ADS_CONVERSION_ID || GA_MEASUREMENT_ID;
  const configIds = [primaryTagId, GOOGLE_ADS_CONVERSION_ID, GA_MEASUREMENT_ID]
    .filter((id): id is string => Boolean(id))
    .filter((id, index, list) => list.indexOf(id) === index);

  return `
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function(){window.dataLayer.push(arguments);};
    window.gtag('js', new Date());
    ${configIds
      .map((id) => `window.gtag('config', ${JSON.stringify(id)}, { 'send_page_view': ${id === GOOGLE_ADS_CONVERSION_ID ? "false" : "true"} });`)
      .join("\n    ")}
  `;
}

function buildMatomoInitScript(): string {
  const url = MATOMO_URL?.endsWith("/") ? MATOMO_URL : `${MATOMO_URL}/`;

  return `
    var _paq = window._paq = window._paq || [];
    _paq.push(['setTrackerUrl', ${JSON.stringify(`${url}matomo.php`)}]);
    _paq.push(['setSiteId', ${JSON.stringify(MATOMO_SITE_ID)}]);
    _paq.push(['trackPageView']);
    _paq.push(['enableLinkTracking']);
    (function() {
      var u=${JSON.stringify(url)};
      var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
      g.async=true; g.src=u+'matomo.js';
      if (s && s.parentNode) s.parentNode.insertBefore(g,s);
    })();
  `;
}

function MarketingTrackingScripts() {
  const primaryTagId = GOOGLE_TAG_ID || GOOGLE_ADS_CONVERSION_ID || GA_MEASUREMENT_ID;

  return (
    <>
      {primaryTagId ? (
        <>
          <Script src={`https://www.googletagmanager.com/gtag/js?id=${primaryTagId}`} strategy="afterInteractive" />
          <Script id="ugcookie-google-tag" strategy="afterInteractive" dangerouslySetInnerHTML={{ __html: buildGoogleTagInitScript() }} />
        </>
      ) : null}
      {MATOMO_URL && MATOMO_SITE_ID ? (
        <Script id="ugcookie-matomo" strategy="afterInteractive" dangerouslySetInnerHTML={{ __html: buildMatomoInitScript() }} />
      ) : null}
    </>
  );
}

function trackLeadConversion(form: LeadFormState, tracking: TrackingParams) {
  if (typeof window === "undefined") return;
  const value = getLeadValueFromBudget(form.budget);
  const eventParams = {
    event_category: "lead",
    event_label: "UGCookie - Agence UGC",
    form_name: "agence_ugc",
    value,
    currency: "EUR",
    budget: form.budget,
    sector: form.sector,
    timing: form.timing,
    ...tracking,
  };

  if (typeof window.gtag === "function") {
    window.gtag("set", "user_data", {
      email: form.email,
      phone_number: form.phone || undefined,
    });
    window.gtag("event", "generate_lead", eventParams);
    if (GOOGLE_ADS_LEAD_SEND_TO) {
      window.gtag("event", "conversion", {
        send_to: GOOGLE_ADS_LEAD_SEND_TO,
        value,
        currency: "EUR",
        transaction_id: `ugc-lead-${Date.now()}`,
      });
    }
  }

  if (window._paq && typeof window._paq.push === "function") {
    window._paq.push(["trackEvent", "Lead", "generate_lead", "UGCookie - Agence UGC", value]);
  }
}

// ---------------------------------------------------------------------------
// RotatingWord — mot qui tourne dans le titre du hero (dynamique)
// ---------------------------------------------------------------------------

function RotatingWord({ words }: { words: string[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % words.length), 2200);
    return () => window.clearInterval(id);
  }, [words.length]);

  return (
    // inline-grid : tous les mots occupent la même cellule, la largeur est
    // celle du mot le plus long — aucun décalage de mise en page au changement.
    <span className="relative inline-grid text-left align-bottom">
      {words.map((w, i) => (
        <span
          key={w}
          aria-hidden={i !== index || undefined}
          className={`col-start-1 row-start-1 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite] transition-all duration-500 ${
            i === index ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 -translate-y-4"
          }`}
        >
          {w}.
        </span>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Orb — decorative blur ball
// ---------------------------------------------------------------------------

function Orb({ className = "" }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`absolute rounded-full blur-3xl pointer-events-none ${className}`}
    />
  );
}

// ---------------------------------------------------------------------------
// VideoPlaceholder
// ---------------------------------------------------------------------------

function VideoPlaceholder({
  ratio = "16/9",
  label = "",
  className = "",
}: {
  ratio?: string;
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={`relative rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 to-secondary/20 ${className}`}
      style={{ aspectRatio: ratio }}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
        <div className="w-16 h-16 rounded-full bg-background/80 flex items-center justify-center shadow-lg">
          <Play className="h-7 w-7 text-primary ml-1" />
        </div>
        {label && (
          <span className="text-sm text-muted-foreground bg-background/60 px-3 py-1 rounded-full">
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Creator card
// ---------------------------------------------------------------------------

type Creator = {
  photo: string;
  name: string;
  specialty: string;
  country: string;
};

function CreatorCard({
  creator,
  onClick,
  className = "",
}: {
  creator: Creator;
  onClick?: () => void;
  className?: string;
}) {
  const firstName = creator.name.split(" ")[0];
  return (
    <div
      className={`creator-card group relative w-44 flex-shrink-0 rounded-[1.75rem] overflow-hidden cursor-pointer shadow-lg hover:rotate-0 hover:scale-[1.03] hover:shadow-2xl hover:ring-2 hover:ring-primary/60 hover:ring-offset-2 hover:ring-offset-background hover:z-10 ${className}`}
      style={{ aspectRatio: "3/4" }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      aria-label={onClick ? `Voir le profil de ${firstName}` : undefined}
    >
      {/* Photo plein format */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={creator.photo}
        alt={firstName}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      {/* Spécialité — pastille blanche avec point dégradé */}
      <div className="absolute top-3 left-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold text-gray-900 shadow-sm backdrop-blur-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-primary to-secondary" />
          {creator.specialty}
        </span>
      </div>
      {/* Drapeau — haut droit */}
      <div className="absolute top-3 right-3 text-base leading-none drop-shadow-md select-none">
        {creator.country}
      </div>
      {/* Gradient overlay bas — plus profond pour lisibilité */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      {/* Prénom vérifié + invitation qui se révèle au survol */}
      <div className="absolute bottom-3.5 inset-x-0 flex items-end justify-between px-3.5">
        <p className="flex items-center gap-1 text-white text-lg font-bold leading-tight drop-shadow">
          {firstName}
          <BadgeCheck className="h-4 w-4 fill-primary text-white" aria-label="Créateur vérifié" />
        </p>
        {onClick && (
          <span className="translate-y-1 text-[10px] font-semibold text-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-90">
            Voir →
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Creator modal
// ---------------------------------------------------------------------------

function CreatorModal({
  creator,
  onClose,
  onRequestContact,
}: {
  creator: Creator;
  onClose: () => void;
  onRequestContact: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Profil créateur : ${creator.name}`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Modal */}
      <div className="relative bg-card rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
        <button
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60 transition-colors"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>
        {/* Photo */}
        <div className="relative overflow-hidden" style={{ aspectRatio: "3/4", maxHeight: "50vh" }}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={creator.photo}
            alt={creator.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-4 right-10">
            <p className="text-white font-bold text-xl leading-tight">{creator.name}</p>
            <p className="text-white/70 text-sm">{creator.specialty} · {creator.country}</p>
          </div>
        </div>
        {/* Info */}
        <div className="p-5 space-y-3">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Portfolio et exemples de productions disponibles sur demande. Contactez-nous pour
            découvrir les créations de{" "}
            <strong>{creator.name.split(" ")[0]}</strong>.
          </p>
          <Button className="w-full btn-primary-gradient text-sm" onClick={onRequestContact}>
            Discutons de votre projet
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

// Formats produits par les créateurs — chips défilantes au-dessus du carrousel.
// Les classes de teinte sont écrites en toutes lettres (pas d'interpolation) :
// Tailwind ne génère que les classes présentes littéralement dans le source.
const CREATOR_FORMATS: { label: string; icon: LucideIcon; tile: string }[] = [
  { label: "Unboxing", icon: Package, tile: "bg-warning/15 text-warning" },
  { label: "GRWM", icon: Sparkles, tile: "bg-secondary/15 text-secondary" },
  { label: "Review produit", icon: Star, tile: "bg-primary/15 text-primary" },
  { label: "Témoignage", icon: Quote, tile: "bg-success/15 text-success" },
  { label: "Tuto", icon: GraduationCap, tile: "bg-primary/15 text-primary" },
  { label: "Before / After", icon: ArrowLeftRight, tile: "bg-secondary/15 text-secondary" },
  { label: "Challenge", icon: Zap, tile: "bg-warning/15 text-warning" },
  { label: "Haul", icon: ShoppingBag, tile: "bg-secondary/15 text-secondary" },
  { label: "Day in my life", icon: Sun, tile: "bg-success/15 text-success" },
  { label: "Interview expert", icon: Mic, tile: "bg-primary/15 text-primary" },
];

const CREATORS_ROW1: Creator[] = [
  { photo: "https://storage.tally.so/private/f0294d73-7a00-49a8-8e2f-a26f4bc060ff.png?id=PvR0R1&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IlB2UjBSMSIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc2NTkxMzQ1N30.8T9SqqkxHgAEuKyc_ZTRm8LQMWv89HkbSgy2eXg0f_Q&signature=b698080665ca23e00a9031e90f8a3821f3fed9ed2371bb839411cbf71fae3489", name: "Cindy Steenkeste", specialty: "Lifestyle", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/IMG_9738.png?id=Rv2EQ4&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IlJ2MkVRNCIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc2NzM1Mjg1N30.CuXmVzDzMkecKgcDH_d4abmFv4cfUWWCxsfKlTzD2oA&signature=e2f6e757c6bef04dd082b3c97a17cc7eb8be641d87bee0fe83dadde1e713eb51", name: "Dylan Brasseur", specialty: "Mode", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/DSC01841.jpeg?id=5v6kdv&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjV2NmtkdiIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc1NjI5MDM0NH0.aqusqNhgC4IqzE7z9FSABpDcyIAHJwefEzSHoNNxxDk&signature=6df2d3f51c084b53ffc7e11ca28eff117d6100a1855f08194075c4cdb8ea9335", name: "Candice Noel", specialty: "Beauté", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/inbound5626555210732717888.jpg?id=XM1vMP&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IlhNMXZNUCIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc1NzU3OTMwNn0.gil13DAxVUw7P_HlxbhWZUEJ81hz3Ro_ch9aC44sBX0&signature=ab01d4a74c555884f25d7411fdfbb33445d92ff3ef9877cf91f9d505c42aaaec", name: "Mickael Collart", specialty: "Lifestyle", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/IMG_8478.jpeg?id=DZvv2R&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IkRadnYyUiIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc3MTA3NzMxOH0.W0B_GQxd_mEpE_XmYHlN-9zqEJzs8exj35F1N3UZO2k&signature=e3dc55e6db99d9d0ae175849d9bee488dae30f2608d708d7ae224140e047e102", name: "Delphine Depoorter", specialty: "Beauty", country: "🇧🇪" },
  { photo: "https://storage.tally.so/private/IMG_7306.jpeg?id=Vo6aZv&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IlZvNmFadiIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc2NTM4MDY3MX0.QTVZTfkjVPJDKFHsCGWii_4Gowpd17VPqX16VtWL_fg&signature=f90c79425aafe2572ded81afaa0b83f1c5fea5a5d26864dafb90aaada568d00e", name: "Antoine Brault", specialty: "Lifestyle", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/dji_mimo_20260203_165944_20260203165945_1770134443799_photo.jpeg?id=RVLAWl&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IlJWTEFXbCIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc3MDg4ODcwNH0.a37dxSdZBIh_EMnsnwF0vA2Z2IB227I5VOgW_fyYsg4&signature=3c4656c1a8ba316b6e1eee4de8889062ad9c008297971bf11eb1490f5a15f0ce", name: "Shanti Stynen", specialty: "Beauty", country: "🇧🇪" },
  { photo: "https://storage.tally.so/private/IMG_3006.jpeg?id=5GK97b&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjVHSzk3YiIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc1NjMzNDY1N30.VeF-lE7LUG2zwFmqjQo-WLTIYd7L2fLeF161jAdtjoY&signature=ec7536fb66bc1012928ef1950081fc5fcd23255ed9fa6b89f9d5e9fb6eaed1aa", name: "Maxime Lucand", specialty: "Tech", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/inbound4027164795179832610.jpg?id=0DMxXN&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjBETXhYTiIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc3MDgwODEyMX0.fs1aj2yS5NlX1xdC3b5MzxjqOerQ3GfXNV5zzf1Gi2s&signature=a0e571ce48cfc21f54f580c216de53014857f09da0ccf0b5b9333a52abc4830f", name: "Assya Atonga", specialty: "Beauty", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/IMG_0763.jpeg?id=PYMGWe&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IlBZTUdXZSIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc1NjMwMTMyMn0.zTpmdr41hiP8IXQheKJXZl8yauJ6pWdTY1n46XRC17Y&signature=c779e18f18e748a304b740009d3eeb8ae4e6a3d4820ee9a11869c62d28b425ff", name: "Tristan Valentin", specialty: "Sport", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/IMG_2782.jpeg?id=4pkYKd&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjRwa1lLZCIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc3MDgyNjIzMn0.0aNo94AQjDHvnes4s2xws7YbPLNLc6Funw-bV-lN52U&signature=77804b1cffe632c270364e53ccf5fc318a428b2f19f1ead562b898c92f8002f6", name: "Lorie Carrière", specialty: "Beauty", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/IMG_1645.jpeg?id=e21Rdl&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6ImUyMVJkbCIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc1NjI4NjU2MX0.q9R5qftS3KHhOxx0gSLqgQq36OM7zIvZFgxDLsH_ZyI&signature=b04cc6f80d8c4376b588fadf840b89ada5e5a985c0bcb48c016458a1a0c57500", name: "Juvenstar Bertrand", specialty: "Lifestyle", country: "🇨🇭" },
];

const CREATORS_ROW2: Creator[] = [
  { photo: "https://storage.tally.so/private/1000075032.png?id=xlp7jE&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InhscDdqRSIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc3MDczNjE0Mn0.AblaIIyISwZK68Z8fVUoD2rp3Szas88Djkoo078sZ6E&signature=dd92b9c6b9ea8f70070936e1ba894673d897caaa9e50dbc617e71ded1d029d72", name: "Mégane Petel", specialty: "Food", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/abce7001-5895-4d5d-9f65-b8747a9987fd_Original.jpeg?id=p4EQaV&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InA0RVFhViIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc2NTQ0NjQzOX0.F7oj2DDtRVZZoXbT2vujwxOzAwgEo_Nd46pTIJP0078&signature=592c27df2422e3b21b2122b0b9ea7c4cc77caea29d54b9403a0dab9aa4a76dd6", name: "Kevin Trombin", specialty: "Lifestyle", country: "🇮🇹" },
  { photo: "https://storage.tally.so/private/IMG_0851.jpeg?id=z1qjaR&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InoxcWphUiIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc3MDcxNDU0M30.AgBpaHkTAgEQhGAsijProON4YEGOgiQ0KPi7zH19lss&signature=c18664e8855a2598632379fa00e1f40456908b10221818712503f82889be27db", name: "Manon Azéma", specialty: "Fashion", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/Design-sans-titre-13-.png?id=PXZ9LQ&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IlBYWjlMUSIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc1ODI2NzE0MH0.oW2upm7wF3KgICJrMKFu1fLf7k1MfhVv-1PSljrVPBI&signature=3d964e994ce022402714b4d81bfacfd9b9ae7a83b84ffdc834df8bd671adb6bc", name: "Sacha Puthiot", specialty: "Lifestyle", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/IMG_4501.JPG?id=6pNkz5&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjZwTmt6NSIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc3MDYzNjk4OH0.JcKJmI1obDxajeNa1of-eWDELyC9I9S6Z0Vs2_7_fPY&signature=45f5828cf2a1722da7d3c02baec69690aace6c304d737a27f2c1407886431b7d", name: "Camille Boutherin", specialty: "Sport", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/1-1.png?id=d2p7zz&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6ImQycDd6eiIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc2MDExMDkyMn0.FcnpSiVX7WuaXMcDyeltJe3Wxhw4ucAPgfadYEkLOvM&signature=b32d14770713f4c212838065723799b5d59f787f307262f8d5971e6df13c3067", name: "Arnaud Vergara", specialty: "Mode", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/tempImagexmOdUm.jpg?id=xlMqdJ&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InhsTXFkSiIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc3MDQ3MTI0NX0.FARP1OFskQqYfT2Coeo222rCYHpzFJo0V2F9gSoXHC0&signature=249edc81091c7602a6fd8171fe7b1f81b1e5f91f978dd02ded2b957f6bed790f", name: "Laurie Rajohnson", specialty: "Beauty", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/alex1.jpg?id=xP6Yb5&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InhQNlliNSIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc1Nzg1NTIyMn0.Fi4wLxWErpe1UV-2byIxlWhvevPmXFyjgLHh-eEa4i0&signature=e74b9825d913f2820506c0b40fe3a571e1947646044449935d06032a8e86bc2b", name: "Alex Ferraro", specialty: "Mode", country: "🇮🇹" },
  { photo: "https://storage.tally.so/private/IMG-20251011-WA0124-1-.jpg?id=8RzjWP&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjhSempXUCIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc3MDQ1NzcwM30.oZWtPsEqYOdZfreU5lJ3i2l11YUgqJZ2CcQTYnUe3oE&signature=4f17a688620225aa11da7fab83e392b88e979b1db4703a3fe6504ca07da40cda", name: "Salma Baydda", specialty: "Beauty", country: "🌍" },
  { photo: "https://storage.tally.so/private/DSC06964_1.jpg?id=OYp6X8&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Ik9ZcDZYOCIsImZvcm1JZCI6Im1ZMTRQVyIsImlhdCI6MTc1NjMxNjQxNn0.0mLS1vp1DFrnF4xuvU41e5ahK794DxH4BY243IVE8LQ&signature=9f913c6be824f3e4124a175523f8971eba0179f4a0a7703b13a75d5e9315321e", name: "Paul Bouchet", specialty: "Mode", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/IMG_7080.jpeg?id=8RaEoz&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjhSYUVveiIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc3MDQ1NDIyOH0.UtPIg5IbHqYQvd3qqQLtcwJ6GFzBU_EtXoBZB8bemgY&signature=9ada92a54a9adfeb84b0f43938d33623f45313bc5c69e506b2a70a475438148d", name: "Elodie Lourenco", specialty: "Lifestyle", country: "🇫🇷" },
  { photo: "https://storage.tally.so/private/79b8d8ddb714ba36a077e7a7c7626feb456dfa22.jpeg?id=aDZ9Jy&accessToken=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6ImFEWjlKeSIsImZvcm1JZCI6Im5XTHkxZSIsImlhdCI6MTc1NzQ5Mjk1N30.XRqeRZCKu938DBzWfmOx5zv_SnbC2HPcUgjoTzcymzU&signature=b587d0e6b120125566458992a9177a93cec1b3394ca36bd02708920fd69331cc", name: "Anthony Tuffier", specialty: "Lifestyle", country: "🇫🇷" },
];

type CaseStudy = {
  sector: string;
  emoji: string;
  results: { value: string; label: string }[];
  production: string;
  duration: string;
  platforms: string[];
};

const CASE_STUDIES: CaseStudy[] = [
  {
    sector: "EDF · Énergie",
    emoji: "⚡",
    results: [{ value: "+15M", label: "vues générées sur une seule vidéo" }],
    production: "1 vidéo · 1 créateur · viral",
    duration: "1 vidéo",
    platforms: ["TikTok"],
  },
  {
    sector: "Formation en ligne",
    emoji: "🎓",
    results: [{ value: "80", label: "ventes générées en 30 jours" }],
    production: "6 vidéos · 3 créateurs",
    duration: "1 mois",
    platforms: ["Meta", "TikTok", "Instagram"],
  },
  {
    sector: "E-commerce",
    emoji: "🛍️",
    results: [{ value: "×3.4", label: "retour sur investissement pub" }],
    production: "12 vidéos · 4 créateurs",
    duration: "30 jours",
    platforms: ["Meta", "TikTok", "Instagram"],
  },
  {
    sector: "Cabaïa · Bagagerie",
    emoji: "🎒",
    results: [{ value: "0,009€", label: "coût par vue, moins d'un centime" }],
    production: "−65% vs. benchmark marché",
    duration: "campagne",
    platforms: ["TikTok", "Meta"],
  },
  {
    sector: "Sport & Fitness",
    emoji: "🏋️",
    results: [{ value: "+42%", label: "taux d'engagement vs. contenu studio" }],
    production: "8 vidéos · 2 créateurs",
    duration: "2 semaines",
    platforms: ["Instagram", "TikTok"],
  },
  {
    sector: "Santé & Bien-être",
    emoji: "🌿",
    results: [{ value: "×2.1", label: "taux de conversion landing page" }],
    production: "4 vidéos · 2 créatrices",
    duration: "1 mois",
    platforms: ["Meta", "YouTube"],
  },
];

// Mini-tuiles plateformes aux couleurs des marques (mêmes codes que l'orbite
// de VideoShowcase), utilisées dans les cartes résultats.
const PLATFORM_TILES: Record<string, { icon: LucideIcon; tile: string }> = {
  Meta:      { icon: InfinityIcon,  tile: "bg-[#0866FF]" },
  TikTok:    { icon: Music2,        tile: "bg-black" },
  Instagram: { icon: InstagramIcon, tile: "bg-gradient-to-br from-[#F9CE34] via-[#EE2A7B] to-[#6228D7]" },
  YouTube:   { icon: Youtube,       tile: "bg-[#FF0000]" },
};

const FAQS = [
  {
    q: "Combien ça coûte ?",
    a: "Chaque projet est chiffré sur mesure selon le volume de vidéos, les formats, la licence d'utilisation et le niveau d'accompagnement (organique, ads, production, montage). Décrivez votre besoin via le formulaire : vous recevez une proposition détaillée et gratuite sous 48h, sans engagement.",
  },
  {
    q: "Combien de temps pour recevoir mes vidéos ?",
    a: "La majorité des livrables sont disponibles en 7 à 10 jours ouvrés après validation du brief. Les campagnes urgentes peuvent être traitées en 5 jours sur demande.",
  },
  {
    q: "Quels droits d'utilisation sont inclus ?",
    a: "Chaque livrable inclut une licence d'exploitation pour vos usages paid ads et organique sur les plateformes digitales (Meta, TikTok, Instagram, YouTube, etc.). La durée et les conditions exactes sont précisées au contrat selon les créateurs.",
  },
  {
    q: "Puis-je demander des modifications ?",
    a: "Oui, un ou plusieurs rounds de révisions sont inclus, le nombre exact est précisé dans la proposition selon la prestation. Un brief précis dès le départ limite les allers-retours, notre équipe vous accompagne pour le cadrer avant le tournage.",
  },
  {
    q: "Comment choisissez-vous les créateurs ?",
    a: "Chaque créateur est sélectionné sur dossier : portfolio, équipement, régularité de publication et maîtrise des formats courts. Nous les matchons ensuite à votre marque selon le secteur, le ton et la cible. Vous validez le casting avant le tournage.",
  },
];

const CLIENTS = [
  { name: "Naturhouse",        logo: "/logos/naturhouse.png"    },
  { name: "EDF",               logo: "/logos/edf.png"           },
  { name: "Auchan",            logo: "/logos/auchan.svg"        },
  { name: "HelloFresh",        logo: "/logos/hellofresh.png"    },
  { name: "Saint-Gobain",      logo: "/logos/saint-gobain.png" },
  { name: "Ninja",             logo: "/logos/ninja.svg"         },
  { name: "Entrepreneurs.com", logo: "/logos/entrepreneurs.png" },
  { name: "Les Mousquetaires", logo: "/logos/mousquetaires.png" },
  { name: "BMW",               logo: "/logos/bmw.png"           },
  { name: "Depilncare",        logo: "/logos/depilncare.png"    },
  { name: "Cabaïa",            logo: "/logos/cabaia.svg"        },
  { name: "Animalis",          logo: "/logos/animalis.png"      },
  { name: "Open Work",         logo: "/logos/openwork.png"      },
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AgenceUGCPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState<LeadFormState>({
    name: "",
    email: "",
    company: "",
    phone: "",
    budget: "",
    sector: "",
    timing: "",
    message: "",
    rgpd: false,
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [liveCreators, setLiveCreators] = useState<Creator[]>([]);
  const contactRef = useRef<HTMLDivElement>(null);
  const creatorsRef = useRef<HTMLDivElement>(null);

  // Créateurs mis en avant par l'admin (/admin/landing-creators). En dessous
  // de 6 profils sélectionnés, on garde les listes historiques en dur pour ne
  // jamais afficher un carrousel clairsemé.
  useEffect(() => {
    persistTrackingParamsFromUrl();
    fetch('/api/public/featured-creators')
      .then((r) => r.json())
      .then((data: Creator[]) => { if (Array.isArray(data)) setLiveCreators(data); })
      .catch(() => {});
  }, []);
  const useLive = liveCreators.length >= 6;
  const creatorsRow1 = useLive ? liveCreators.slice(0, Math.ceil(liveCreators.length / 2)) : CREATORS_ROW1;
  const creatorsRow2 = useLive ? liveCreators.slice(Math.ceil(liveCreators.length / 2)) : CREATORS_ROW2;

  function scrollTo(ref: React.RefObject<HTMLDivElement | null>) {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMenuOpen(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.rgpd) return;
    setStatus("loading");
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const tracking = getStoredTrackingParams();
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, tracking }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        trackLeadConversion(form, tracking);
        setStatus("success");
        setForm({ name: "", email: "", company: "", phone: "", budget: "", sector: "", timing: "", message: "", rgpd: false });
      } else {
        setStatus("error");
      }
    } catch {
      clearTimeout(timeoutId);
      setStatus("error");
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingTrackingScripts />

      {/* ------------------------------------------------------------------ */}
      {/* 1. Navbar                                                            */}
      {/* ------------------------------------------------------------------ */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/60 shadow-sm supports-[backdrop-filter]:bg-white/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Play className="h-4 w-4 text-white ml-0.5" />
              </div>
              <span className="font-bold text-lg text-gray-900">UGC Cookie <span className="text-primary">Agence</span></span>
            </div>

            <nav className="hidden md:flex items-center gap-6 text-sm">
              <button onClick={() => scrollTo(creatorsRef)} className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                Nos créateurs
              </button>
              <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                Nos offres
              </button>
              <button onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                FAQ
              </button>
              <Button size="sm" onClick={() => scrollTo(contactRef)}>
                Devis gratuit
              </Button>
            </nav>

            <button className="md:hidden p-2 text-gray-900" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-2">
              <button onClick={() => scrollTo(creatorsRef)} className="block w-full text-left px-3 py-3 text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors">Nos créateurs</button>
              <button onClick={() => { document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }); setMenuOpen(false); }} className="block w-full text-left px-3 py-3 text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors">Nos offres</button>
              <button onClick={() => { document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); setMenuOpen(false); }} className="block w-full text-left px-3 py-3 text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors">FAQ</button>
              <div className="pt-2 pb-3">
                <Button className="w-full" onClick={() => scrollTo(contactRef)}>Devis gratuit</Button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* 2. Hero — dark split layout                                         */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        {/* Dot grid overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,hsla(245,85%,65%,0.07)_1px,transparent_1px)] bg-[size:24px_24px]" />
        {/* Decorative orbs — flottement lent pour un fond vivant */}
        <Orb className="w-96 h-96 bg-primary/15 -top-24 -left-24 animate-[floatY_9s_ease-in-out_infinite]" />
        <Orb className="w-72 h-72 bg-secondary/15 top-1/2 -right-24 animate-[floatY_12s_ease-in-out_infinite_reverse]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

            {/* Left col */}
            <div className="relative z-10 flex-1 text-center lg:text-left">
              <Reveal>
              <span className="mb-6 inline-flex items-center rounded-full bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary">
                <span className="relative mr-2 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                </span>
                Agence UGC · +150 marques accompagnées
              </span>
              </Reveal>

              {/* Titre — une seule idée, un mot rotatif pour le dynamisme */}
              <Reveal delayMs={100} className="mb-6">
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.08] tracking-tight text-foreground">
                  Des vidéos UGC
                  <br />
                  qui font <RotatingWord words={["scroller", "cliquer", "acheter"]} />
                </h1>
              </Reveal>

              <Reveal delayMs={200}>
                <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-9 leading-relaxed">
                  Vos pubs s&apos;essoufflent ? On produit le contenu qui convertit,
                  livré en 7 à 10 jours, prêt à diffuser.
                </p>
              </Reveal>

              <Reveal delayMs={300}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" className="group text-base px-8 btn-primary-gradient transition-transform hover:scale-[1.03]" onClick={() => scrollTo(contactRef)}>
                  Obtenir ma proposition gratuite
                  <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8"
                  onClick={() => document.getElementById('creations')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Voir nos créations
                </Button>
              </div>
              <p className="text-xs text-muted-foreground/70 mt-3 mb-8 text-center lg:text-left">
                Réponse sous 48h · Sans engagement
              </p>
              </Reveal>

              {/* Social proof : avatars de vrais créateurs, une seule ligne */}
              <Reveal delayMs={400} className="flex items-center gap-3 justify-center lg:justify-start">
                <div className="flex -space-x-3">
                  {creatorsRow1.slice(0, 4).map((c) => (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      key={c.name}
                      src={c.photo}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-10 w-10 rounded-full border-2 border-background object-cover shadow-sm"
                    />
                  ))}
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-background bg-gradient-to-br from-primary to-secondary text-[10px] font-bold text-white shadow-sm">
                    500+
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold leading-tight text-foreground">500+ créateurs vérifiés</div>
                  <div className="text-xs text-muted-foreground">France & Europe · 150+ marques accompagnées</div>
                </div>
              </Reveal>
            </div>

            {/* Right col — mur vidéo diagonal (façon Moggo / Youdji) :
                3 colonnes inclinées à -8° qui défilent verticalement en
                directions alternées + parallaxe souris. Sur desktop le mur
                déborde de sa boîte et n'est recadré que par la section hero.
                Une seule pastille flottante par-dessus, à fond sombre opaque
                pour rester lisible sur les vidéos en mouvement. */}
            <Reveal delayMs={200} className="relative flex-shrink-0 w-full max-w-md lg:w-[520px] lg:max-w-none" aria-hidden="true">

              <HeroVideoCarousel className="h-[26rem] sm:h-[30rem] lg:h-[34rem] rounded-[2rem] lg:rounded-none" />

              {/* Pastille résultat — fond sombre opaque, lisible sur le mur */}
              {/* Fond opaque obligatoire : la pastille passe au-dessus des
                  vidéos en mouvement, toute transparence la rend illisible */}
              <div className="absolute bottom-5 left-3 z-20 flex items-center gap-3 rounded-2xl border border-border/60 bg-card px-4 py-3 shadow-2xl lg:-left-8 lg:bottom-10">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary">
                  <Play className="ml-0.5 h-4 w-4 text-white" />
                </span>
                <div>
                  <p className="text-sm font-black leading-none text-foreground">+15M de vues</p>
                  <p className="mt-1 text-[11px] leading-none text-muted-foreground">sur une seule vidéo · cas client EDF</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 2.05. Ruban défilant incliné — transition hero → logos             */}
      {/* ------------------------------------------------------------------ */}
      <div className="relative z-20 -mt-5 -mb-5 rotate-[-1.5deg] scale-[1.02]" aria-hidden="true">
        <div className="overflow-hidden bg-gradient-to-r from-primary to-secondary py-3 shadow-lg">
          <div className="flex w-max animate-[scrollLeft_30s_linear_infinite] will-change-transform">
            {(() => {
              const items = [
                "Vidéos UGC qui convertissent",
                "500+ créateurs vérifiés",
                "Livraison en 7 à 10 jours",
                "TikTok · Meta · Instagram · YouTube",
                "150+ marques accompagnées",
              ];
              return [...items, ...items].map((t, i) => (
                <span
                  key={i}
                  className="flex items-center whitespace-nowrap text-xs sm:text-sm font-black uppercase tracking-[0.15em] text-white"
                >
                  <span className="px-5">{t}</span>
                  <span className="text-white/50">✦</span>
                </span>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* 2.1. Logos clients — fond clair, couleurs originales              */}
      {/* ------------------------------------------------------------------ */}
      <section className="pt-16 pb-14 bg-background overflow-hidden">
        <Reveal className="text-center mb-10 px-4">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-muted-foreground/60 mb-3">
            Ils nous font confiance
          </p>
          <p className="text-2xl sm:text-3xl font-bold tracking-tight">
            150+ marques, de la start-up au{" "}
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">grand groupe</span>
          </p>
        </Reveal>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-background to-transparent" />
          {/* Cartes logos : grises au repos, couleur au survol, défilement en
              pause quand la souris est dessus */}
          <div className="flex w-max items-center gap-4 px-4 py-2 animate-[scrollLeft_45s_linear_infinite] will-change-transform hover:[animation-play-state:paused]">
            {[...CLIENTS, ...CLIENTS].map((c, i) => (
              <div
                key={i}
                aria-hidden={i >= CLIENTS.length || undefined}
                className="group flex h-[4.5rem] w-auto min-w-[7rem] flex-shrink-0 items-center justify-center rounded-2xl border border-border/60 bg-card px-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-md"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={c.logo}
                  alt={c.name}
                  title={c.name}
                  loading="lazy"
                  decoding="async"
                  className="max-h-11 w-auto max-w-[150px] object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 2.5. Video Showcase                                                 */}
      {/* ------------------------------------------------------------------ */}
      <VideoShowcase />

      {/* ------------------------------------------------------------------ */}
      {/* 3. Agitate                                                           */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-muted/30 overflow-hidden">
        <Orb className="w-80 h-80 bg-primary/10 -top-24 -right-24 animate-[floatY_11s_ease-in-out_infinite]" />

        <div className="relative max-w-4xl mx-auto">
          <Reveal className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Vous vous{" "}
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite]">
                reconnaissez
              </span>{" "}
              ?
            </h2>
          </Reveal>

          {/* 5 cartes douleur + 1 carte solution en dégradé qui clôt la grille */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              "Vous passez des heures à chercher des créateurs qui ne répondent pas, ou qui livrent hors brief.",
              "Vous recevez des vidéos inutilisables que vous devez refaire monter par quelqu'un d'autre.",
              "Vous n'avez aucune idée de quel angle, quel hook, quel format va vraiment performer.",
              "Vous publiez du contenu sans savoir s'il vend vraiment, ou juste si ça génère de l'engagement vide.",
              "Vous jonglez avec 3 prestataires différents pour un seul et même besoin.",
            ].map((pain, i) => (
              <Reveal key={i} delayMs={i * 80} className="h-full">
                <div className="flex h-full flex-col gap-4 rounded-2xl border border-border/60 bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-destructive/30 hover:shadow-lg">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-destructive/10">
                    <X className="h-4 w-4 text-destructive" />
                  </div>
                  <p className="text-muted-foreground text-base leading-relaxed">{pain}</p>
                </div>
              </Reveal>
            ))}

            {/* Carte solution — même gabarit, elle retourne le discours */}
            <Reveal delayMs={400} className="h-full">
              <button
                type="button"
                onClick={() => scrollTo(contactRef)}
                className="group flex h-full w-full flex-col gap-4 rounded-2xl bg-gradient-to-br from-primary to-secondary p-6 text-left shadow-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/20 border border-white/30">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
                <p className="text-white text-base font-semibold leading-relaxed">
                  Vous n&apos;avez pas à gérer tout ça seul. On s&apos;occupe de tout,
                  du brief à la livraison.
                </p>
                <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-bold text-white/90">
                  Discutons-en
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 4. Process — 4 étapes                                               */}
      {/* ------------------------------------------------------------------ */}
      <section id="process" className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <Orb className="w-96 h-96 bg-primary/10 -top-32 -left-32 animate-[floatY_10s_ease-in-out_infinite]" />
        <Orb className="w-72 h-72 bg-secondary/10 -bottom-24 -right-24 animate-[floatY_13s_ease-in-out_infinite_reverse]" />

        <div className="relative max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              <Rocket className="h-3.5 w-3.5" /> Notre process
            </span>
            <h2 className="mb-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              De votre idée à votre vidéo en{" "}
              <span className="relative inline-block px-2.5">
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 inset-y-0.5 -rotate-1 rounded-lg bg-gradient-to-r from-primary to-secondary bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite]"
                />
                <span className="relative text-white">10 jours</span>
              </span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Stratégie → Sélection → Tournage → Livraison. Simple et rapide.
            </p>
          </Reveal>

          <div className="relative">
            {/* Ligne de progression reliant les étapes (desktop) : du primary
                au vert "livré", même logique que le pipeline du dashboard */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-[12%] right-[12%] top-12 hidden h-1 rounded-full bg-gradient-to-r from-primary via-secondary to-success opacity-25 lg:block"
            />
            <div className="grid grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { step: "01", day: "J0",     title: "Brief & Stratégie", desc: "Vous décrivez votre cible et vos objectifs en 15 min. Notre creative strategist identifie déjà les angles et les hooks qui ont le plus de chances de performer sur votre marché.", icon: Brain,       tint: "bg-primary/10",   text: "text-primary" },
                { step: "02", day: "J1–J2",  title: "Casting",           desc: "On sélectionne les créateurs parfaits dans notre réseau de 500+ profils vérifiés.",                                                                                               icon: Target,      tint: "bg-secondary/10", text: "text-secondary" },
                { step: "03", day: "J3–J8",  title: "Production",        desc: "Les créateurs tournent les vidéos selon votre brief. Vous pouvez suivre l'avancement.",                                                                                            icon: Clapperboard, tint: "bg-warning/10",  text: "text-warning" },
                { step: "04", day: "J7–J10", title: "Livraison",         desc: "Vous recevez les fichiers HD + la licence d'utilisation. Prêts à diffuser en paid et organique.",                                                                                  icon: PackageCheck, tint: "bg-success/10",  text: "text-success" },
              ].map((item, i) => (
                <Reveal key={item.step} delayMs={i * 120} className="relative h-full">
                  <div className="group relative z-10 flex h-full flex-col rounded-2xl border border-border/60 bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/30 hover:shadow-xl">
                    {/* Numéro fantôme en fond */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute right-5 top-3 text-6xl font-black tracking-tighter text-foreground/[0.05] transition-colors duration-300 group-hover:text-primary/10"
                    >
                      {item.step}
                    </span>
                    {/* Tuile icône teintée, façon dashboard de l'app */}
                    <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:-rotate-3 group-hover:scale-110 ${item.tint}`}>
                      <item.icon className={`h-6 w-6 ${item.text}`} />
                    </div>
                    <div className={`mb-3 inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${item.tint} ${item.text}`}>
                      {item.day}
                    </div>
                    <h3 className="mb-2 text-lg font-bold">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Button size="lg" className="group btn-primary-gradient text-base px-8 transition-transform hover:scale-[1.03]" onClick={() => scrollTo(contactRef)}>
              Démarrer mon brief
              <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
            <p className="text-xs text-muted-foreground mt-3">15 minutes pour lancer votre production</p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 5. Réseau de créateurs — REFONTE                                    */}
      {/* ------------------------------------------------------------------ */}
      <section
        id="createurs"
        ref={creatorsRef}
        className="py-14 bg-gradient-to-br from-primary/5 via-background to-secondary/5 overflow-hidden"
      >
        {/* Titre — dans le container */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          <Reveal className="text-center">
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              <Users className="h-3.5 w-3.5" /> Nos créateurs
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
              Des créateurs qui font{" "}
              <span className="relative inline-block px-2.5">
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 inset-y-0.5 -rotate-1 rounded-lg bg-gradient-to-r from-primary to-secondary bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite]"
                />
                <span className="relative text-white">acheter</span>
              </span>{" "}
              vos clients
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Chaque créateur est sélectionné et accompagné par notre équipe, en <strong>France</strong> 🇫🇷 et à <strong>l&apos;international</strong> 🌍
            </p>
          </Reveal>
        </div>

        {/* Formats produits — chips défilantes, icône teintée façon app */}
        <div className="relative mb-4 overflow-hidden py-1" aria-hidden="true">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-background to-transparent" />
          <div className="flex w-max items-center gap-3 animate-[scrollLeft_32s_linear_infinite] will-change-transform">
            {[...CREATOR_FORMATS, ...CREATOR_FORMATS].map((format, i) => {
              const Icon = format.icon;
              return (
                <span
                  key={i}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-border/60 bg-card py-1.5 pl-2 pr-4 shadow-sm ${
                    i % 2 === 0 ? "rotate-[1.2deg]" : "-rotate-[1.2deg]"
                  }`}
                >
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full ${format.tile}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm font-semibold text-foreground/75">{format.label}</span>
                </span>
              );
            })}
          </div>
        </div>

        {/* Row 1 → pleine largeur, défile vers la gauche en continu */}
        <div className="relative overflow-hidden mb-4">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 z-10 bg-gradient-to-r from-background/80 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 z-10 bg-gradient-to-l from-background/80 to-transparent" />
          <div className="flex gap-5 w-max py-7 animate-[scrollLeft_35s_linear_infinite] will-change-transform">
            {[...creatorsRow1, ...creatorsRow1].map((creator, i) => (
              <CreatorCard
                key={`r1-${i}`}
                creator={creator}
                onClick={i < creatorsRow1.length ? () => setSelectedCreator(creator) : undefined}
                className={i % 2 === 0 ? "rotate-[1.5deg]" : "-rotate-[1.5deg] translate-y-2"}
              />
            ))}
          </div>
        </div>

        {/* Row 2 → pleine largeur, défile vers la droite en continu */}
        <div className="relative overflow-hidden mb-10">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 z-10 bg-gradient-to-r from-background/80 to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 z-10 bg-gradient-to-l from-background/80 to-transparent" />
          <div className="flex gap-5 w-max py-7 animate-[scrollRight_40s_linear_infinite] will-change-transform">
            {[...creatorsRow2, ...creatorsRow2].map((creator, i) => (
              <CreatorCard
                key={`r2-${i}`}
                creator={creator}
                onClick={i < creatorsRow2.length ? () => setSelectedCreator(creator) : undefined}
                className={i % 2 === 0 ? "-rotate-[1.5deg] translate-y-2" : "rotate-[1.5deg]"}
              />
            ))}
          </div>
        </div>

        {/* Pays + texte — dans le container */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {["🇫🇷 France", "🇬🇧 UK", "🇩🇪 Allemagne", "🇧🇪 Belgique", "🇪🇸 Espagne", "🇨🇭 Suisse", "🇮🇹 Italie"].map((c) => (
              <span
                key={c}
                className="cursor-default rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs font-medium text-foreground/80 shadow-sm transition-transform duration-300 hover:-translate-y-0.5"
              >
                {c}
              </span>
            ))}
            <span className="rounded-full bg-gradient-to-r from-primary to-secondary px-3 py-1.5 text-xs font-bold text-white shadow-sm">
              +8 pays
            </span>
          </div>
          {/* Deux appels sur une même ligne : accès portfolios + recrutement */}
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-4 py-2 text-sm text-muted-foreground shadow-sm">
              <Eye className="h-4 w-4 text-primary" />
              Accès aux portfolios créateurs dès votre premier échange
            </span>
            <a
              href="/apply"
              className="group inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary/15"
            >
              Vous êtes créateur ? Rejoignez le réseau
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 6. Résultats — dark metrics redesign                               */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-background overflow-hidden">
        <Orb className="w-96 h-96 bg-primary/10 -top-32 -left-32 animate-[floatY_12s_ease-in-out_infinite]" />
        <Orb className="w-72 h-72 bg-secondary/10 -bottom-24 -right-16 animate-[floatY_9s_ease-in-out_infinite_reverse]" />

        <div className="relative max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-[hsla(158,85%,45%,0.12)] px-3 py-1 text-sm font-semibold text-success">
              <BarChart3 className="h-3.5 w-3.5" /> Résultats vérifiés
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Des résultats mesurables,{" "}
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                campagne après campagne
              </span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Des marques que vous connaissez, des chiffres qu&apos;on peut prouver. Sur Meta, TikTok, Instagram et YouTube.
            </p>
          </Reveal>

          {/* Grille uniforme et aérée : le visuel vient des chiffres en
              dégradé et des mini-tuiles plateformes aux couleurs des marques */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 mb-12">
            {CASE_STUDIES.map((cs, i) => (
              <Reveal key={cs.sector} delayMs={i * 80} className="h-full">
                <div className="group flex h-full flex-col gap-4 rounded-2xl border border-border/60 bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl">
                  {/* Secteur */}
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-lg transition-transform duration-300 group-hover:scale-110">
                      {cs.emoji}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">{cs.sector}</span>
                  </div>
                  {/* Métrique principale — chiffre en dégradé charte */}
                  <div>
                    <div className="mb-1 origin-left bg-gradient-to-r from-primary to-secondary bg-clip-text text-4xl font-black leading-none text-transparent transition-transform duration-300 group-hover:scale-105">
                      {cs.results[0].value}
                    </div>
                    <div className="text-sm text-muted-foreground">{cs.results[0].label}</div>
                  </div>
                  {/* Contexte + plateformes de diffusion */}
                  <div className="mt-auto flex items-center justify-between gap-3 border-t border-border/60 pt-3">
                    <p className="text-xs text-muted-foreground/60">{cs.production}</p>
                    <div className="flex flex-shrink-0 -space-x-1.5">
                      {cs.platforms.map((p) => {
                        const pt = PLATFORM_TILES[p];
                        if (!pt) return null;
                        return (
                          <span
                            key={p}
                            title={p}
                            className={`flex h-6 w-6 items-center justify-center rounded-lg ring-2 ring-card transition-transform duration-300 group-hover:translate-x-0 ${pt.tile}`}
                          >
                            <pt.icon className="h-3 w-3 text-white" strokeWidth={2.5} />
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="text-center text-xs text-muted-foreground/60 mt-4 mb-10">
            * Résultat exceptionnel d&apos;une campagne virale. Résultat typique : 2–4× retour sur investissement publicitaire.
          </p>
          <div className="text-center">
            <Button className="btn-primary-gradient text-base px-8" onClick={() => scrollTo(contactRef)}>
              Je veux ces résultats pour ma marque
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 7. Services & Tarifs — REFONTE (remontée avant l'équipe)            */}
      {/* ------------------------------------------------------------------ */}
      <section id="services" className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-muted/30 overflow-hidden">
        <Orb className="w-96 h-96 bg-primary/10 -top-32 -right-32" />
        <Orb className="w-72 h-72 bg-secondary/10 bottom-0 -left-24" />
        <div className="relative max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              <TrendingUp className="h-3.5 w-3.5" /> Nos offres
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
              Une offre pour{" "}
              <span className="relative inline-block px-2.5">
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 inset-y-0.5 -rotate-1 rounded-lg bg-gradient-to-r from-primary to-secondary bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite]"
                />
                <span className="relative text-white">chaque besoin</span>
              </span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Organique, ads, production, montage : on construit le devis ensemble, selon vos objectifs. Réponse sous 48h.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 md:items-stretch">
            {[
              {
                name: "Organique",
                desc: "Nourrir vos réseaux",
                icon: Leaf,
                // Teintes hsla littérales : les tokens (--primary…) sont des hsl()
                // complets, Tailwind ne peut pas leur injecter d'alpha, un
                // modificateur /NN est ignoré ou sort à pleine opacité.
                tile: "bg-[hsla(158,85%,45%,0.12)] text-success",
                gradient: "bg-[linear-gradient(135deg,hsl(158,85%,45%),hsl(180,70%,42%))]",
                glow: "shadow-[0_10px_28px_hsla(158,85%,45%,0.35)]",
                hoverShadow: "hover:shadow-[0_18px_44px_hsla(158,85%,45%,0.22)] hover:border-[hsla(158,85%,45%,0.5)]",
                features: [
                  "Contenus natifs TikTok, Reels, Shorts",
                  "Créateurs matchés à votre marque",
                  "Pensés pour engager votre audience",
                ],
              },
              {
                name: "Ads",
                desc: "Convertir en paid",
                icon: Target,
                tile: "bg-[hsla(245,85%,65%,0.12)] text-primary",
                gradient: "bg-[linear-gradient(135deg,hsl(245,85%,65%),hsl(265,85%,58%))]",
                glow: "shadow-[0_10px_28px_hsla(245,85%,65%,0.4)]",
                hoverShadow: "hover:shadow-[0_18px_44px_hsla(245,85%,65%,0.25)] hover:border-[hsla(245,85%,65%,0.5)]",
                features: [
                  "Hooks conçus pour stopper le scroll",
                  "Déclinaisons pour vos tests créatifs",
                  "Formats Meta & TikTok Ads",
                ],
              },
              {
                name: "Production",
                desc: "De l'idée au tournage",
                icon: Clapperboard,
                tile: "bg-[hsla(320,75%,62%,0.12)] text-secondary",
                gradient: "bg-[linear-gradient(135deg,hsl(320,75%,62%),hsl(340,85%,60%))]",
                glow: "shadow-[0_10px_28px_hsla(320,75%,62%,0.38)]",
                hoverShadow: "hover:shadow-[0_18px_44px_hsla(320,75%,62%,0.24)] hover:border-[hsla(320,75%,62%,0.5)]",
                features: [
                  "Casting parmi 500+ créateurs",
                  "Brief & script cadrés par l'équipe",
                  "Envoi produit et suivi de tournage",
                ],
              },
              {
                name: "Montage",
                desc: "Prêt à diffuser",
                icon: Scissors,
                tile: "bg-[hsla(42,95%,58%,0.15)] text-warning",
                gradient: "bg-[linear-gradient(135deg,hsl(42,95%,58%),hsl(28,95%,56%))]",
                glow: "shadow-[0_10px_28px_hsla(42,95%,58%,0.4)]",
                hoverShadow: "hover:shadow-[0_18px_44px_hsla(42,95%,58%,0.26)] hover:border-[hsla(42,95%,58%,0.55)]",
                features: [
                  "Cut dynamique et sous-titres",
                  "Déclinaisons 9:16, 1:1, 16:9",
                  "Fichiers HD prêts à diffuser",
                ],
              },
            ].map((offer, i) => (
              <Reveal key={offer.name} delayMs={i * 100}>
                <div className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card p-6 pt-7 transition-all duration-300 hover:-translate-y-2 ${offer.hoverShadow}`}>
                  {/* Liseré dégradé en tête de carte */}
                  <div aria-hidden="true" className={`absolute inset-x-0 top-0 h-1 ${offer.gradient}`} />
                  {/* Numéro discret : le contenu de l'offre reste la vedette */}
                  <span aria-hidden="true" className={`absolute right-5 top-5 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${offer.tile}`}>
                    {i + 1}
                  </span>
                  <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl text-white transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-110 ${offer.gradient} ${offer.glow}`}>
                    <offer.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-2xl font-bold leading-tight">{offer.name}</h3>
                  <p className="mb-5 text-sm text-muted-foreground">{offer.desc}</p>
                  <ul className="mb-6 space-y-3">
                    {offer.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-[15px] font-medium leading-snug">
                        <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${offer.tile}`}>
                          <CheckCircle2 className="h-3 w-3" />
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <Button
                      className="group/cta w-full"
                      variant="outline"
                      onClick={() => scrollTo(contactRef)}
                    >
                      Nous contacter
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/cta:translate-x-1" />
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Bandeau clé en main : gestion A → Z */}
          <Reveal className="mt-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary to-secondary p-8 sm:p-10 shadow-xl">
              <div aria-hidden="true" className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
              <div aria-hidden="true" className="absolute -bottom-20 left-1/4 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-xl">
                  <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                    <Sparkles className="h-3.5 w-3.5" /> Clé en main
                  </span>
                  <h3 className="text-2xl font-bold text-white sm:text-3xl">
                    On gère tout, de A à Z
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/85 sm:text-base">
                    Stratégie, casting, brief, tournage, montage : vos campagnes sont pilotées de bout en bout et livrées clé en main. Vous validez, on s&apos;occupe du reste.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-1.5 gap-y-2 text-xs font-semibold text-white/90">
                    {["Stratégie", "Casting", "Brief", "Tournage", "Montage", "Livraison"].map((step, j, arr) => (
                      <span key={step} className="flex items-center gap-1.5">
                        <span className="rounded-full bg-white/15 px-2.5 py-1">{step}</span>
                        {j < arr.length - 1 && <ArrowRight className="h-3 w-3 text-white/60" />}
                      </span>
                    ))}
                  </div>
                </div>
                <Button
                  size="lg"
                  className="group flex-shrink-0 bg-white text-primary hover:bg-white/90 transition-transform hover:scale-[1.03]"
                  onClick={() => scrollTo(contactRef)}
                >
                  Discutons de votre campagne
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          </Reveal>

        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 7b. Qui sommes-nous — REFONTE (déplacée après les tarifs)           */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-background overflow-hidden">
        <Orb className="w-80 h-80 bg-secondary/10 -top-24 -left-24" />
        <Orb className="w-96 h-96 bg-primary/10 -bottom-32 -right-32" />
        <div className="relative max-w-5xl mx-auto">
          <Reveal className="text-center mb-12">
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              <Users className="h-3.5 w-3.5" /> L&apos;équipe
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
              On ne se cache pas derrière un{" "}
              <span className="relative inline-block px-2.5">
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 inset-y-0.5 rotate-1 rounded-lg bg-gradient-to-r from-secondary to-primary bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite]"
                />
                <span className="relative text-white">logo</span>
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Deux passionnés,{" "}
              <strong className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">20 ans d&apos;expérience cumulée</strong>{" "}
              en communication et marketing digital. On a créé UGC Cookie parce qu&apos;on en avait assez de voir des marques payer pour du contenu qui ne leur rapporte rien.
            </p>
          </Reveal>

          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            {[
              {
                name: "Cindy",
                role: "Fondatrice · Creative Strategist",
                photo: "/images/team/cindy.jpg",
                pill: "10 ans de créa",
                desc: "Creative strategist et CM depuis 10 ans, missions grands comptes en cheffe de projet et collaborations avec des agences renommées. Avant chaque tournage, elle identifie les angles et les hooks qui performent pour votre marque, puis challenge les créateurs et valide chaque livrable pour que le contenu tourne juste, pas juste joli.",
                skills: ["Stratégie créative", "Hooks & angles", "Direction de créateurs"],
                tilt: "md:rotate-[1.2deg] md:hover:rotate-0",
                chip: "bg-[hsla(245,85%,65%,0.12)] text-primary",
              },
              {
                name: "Thibault",
                role: "Performance & Ads · Meta · Google",
                photo: "/images/team/thibault.jpg",
                pill: "10 ans de paid",
                desc: "Spécialiste du paid depuis 10 ans, il a piloté des budgets ads et des stratégies de génération de leads pour des marques et grands comptes. Il s'assure que vos vidéos ne sont pas juste belles, mais qu'elles convertissent.",
                skills: ["Meta Ads", "Google Ads", "Génération de leads"],
                tilt: "md:-rotate-[1.2deg] md:hover:rotate-0 md:translate-y-2",
                chip: "bg-[hsla(320,75%,62%,0.12)] text-secondary",
              },
            ].map((member, i) => (
              <Reveal key={member.name} delayMs={i * 100}>
                <div
                  className={`group relative h-full rounded-3xl border border-border bg-card p-8 pt-12 transition-all duration-300 hover:-translate-y-1 hover:border-[hsla(245,85%,65%,0.4)] hover:shadow-xl ${member.tilt}`}
                >
                  <span className="absolute right-5 top-5 rounded-full bg-gradient-to-r from-primary to-secondary px-3 py-1 text-[11px] font-bold text-white shadow-md shadow-primary/20">
                    {member.pill}
                  </span>
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="relative">
                      <div
                        aria-hidden="true"
                        className="absolute -inset-3 rounded-full bg-[linear-gradient(135deg,hsla(245,85%,65%,0.3),hsla(320,75%,62%,0.3))] opacity-60 blur-lg transition-opacity duration-300 group-hover:opacity-100"
                      />
                      <div className="relative rounded-full bg-gradient-to-br from-primary to-secondary p-1">
                        <img
                          src={member.photo}
                          alt={member.name}
                          className="h-24 w-24 rounded-full object-cover ring-4 ring-card"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-xl font-bold">{member.name}</div>
                      <div className={`mt-1.5 inline-block rounded-full px-3 py-0.5 text-xs font-semibold ${member.chip}`}>
                        {member.role}
                      </div>
                    </div>
                    <p className="text-sm leading-relaxed text-muted-foreground">{member.desc}</p>
                    <div className="mt-1 flex flex-wrap justify-center gap-2">
                      {member.skills.map((skill) => (
                        <span key={skill} className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground/70">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 9. FAQ                                                               */}
      {/* ------------------------------------------------------------------ */}
      <section id="faq" className="relative py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-muted/30 overflow-hidden">
        <Orb className="w-72 h-72 bg-primary/10 -top-20 -left-20" />
        <Orb className="w-80 h-80 bg-secondary/10 -bottom-24 -right-24" />
        <div className="relative mx-auto grid max-w-5xl gap-10 lg:grid-cols-5 lg:gap-14">
          {/* Colonne titre + relance contact */}
          <div className="lg:col-span-2">
            <Reveal>
              <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                <Sparkles className="h-3.5 w-3.5" /> FAQ
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
                Les questions qu&apos;on{" "}
                <span className="relative inline-block px-2">
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 inset-y-0.5 -rotate-1 rounded-lg bg-gradient-to-r from-primary to-secondary bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite]"
                  />
                  <span className="relative text-white">nous pose</span>
                </span>
              </h2>
              <p className="mb-6 text-muted-foreground">
                Prix, délais, droits, révisions, casting : les réponses aux questions qui reviennent le plus souvent.
              </p>
              <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <p className="text-sm font-semibold">Une autre question ?</p>
                <p className="mb-4 mt-1 text-sm text-muted-foreground">
                  On vous répond sous 48h, sans jargon et sans engagement.
                </p>
                <Button size="sm" className="group btn-primary-gradient" onClick={() => scrollTo(contactRef)}>
                  Posez-la nous
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Accordéon */}
          <div className="lg:col-span-3">
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((faq, i) => (
                <Reveal key={i} delayMs={i * 80}>
                  <AccordionItem
                    value={`faq-${i}`}
                    className="rounded-2xl border border-border bg-card px-5 transition-all duration-300 hover:border-[hsla(245,85%,65%,0.4)] data-[state=open]:border-[hsla(245,85%,65%,0.5)] data-[state=open]:shadow-[0_12px_32px_hsla(245,85%,65%,0.12)]"
                  >
                    <AccordionTrigger className="gap-4 py-5 text-left font-semibold hover:no-underline">
                      <span className="flex items-center gap-3.5">
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[hsla(245,85%,65%,0.12)] text-xs font-black text-primary">
                          0{i + 1}
                        </span>
                        {faq.q}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 pl-[2.9rem] leading-relaxed text-muted-foreground">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                </Reveal>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 10. Formulaire de contact — dark hero style                        */}
      {/* ------------------------------------------------------------------ */}
      <section
        id="contact"
        ref={contactRef}
        className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-background to-secondary/10 overflow-hidden"
      >
        {/* Dot grid */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,hsla(245,85%,65%,0.07)_1px,transparent_1px)] bg-[size:24px_24px]" />
        <Orb className="w-80 h-80 bg-primary/15 -top-24 -right-24 animate-[floatY_10s_ease-in-out_infinite]" />
        <Orb className="w-60 h-60 bg-secondary/15 -bottom-12 -left-12 animate-[floatY_13s_ease-in-out_infinite_reverse]" />

        <div className="relative max-w-5xl mx-auto">
          <div className="grid gap-10 lg:grid-cols-5 lg:gap-14 lg:items-start">
            {/* Colonne accompagnement */}
            <div className="lg:col-span-2">
              <Reveal>
                <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                  Contact
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                  Lancez votre première{" "}
                  <span className="relative inline-block px-2">
                    <span
                      aria-hidden="true"
                      className="absolute inset-x-0 inset-y-0.5 -rotate-1 rounded-lg bg-gradient-to-r from-primary to-secondary bg-[length:200%_auto] animate-[gradientX_5s_ease-in-out_infinite]"
                    />
                    <span className="relative text-white">production UGC</span>
                  </span>
                </h2>
                <p className="mb-8 text-muted-foreground">
                  Décrivez votre marque et vos objectifs. Notre équipe revient sous 48h avec une proposition.
                </p>
                <ul className="mb-8 space-y-3.5">
                  {[
                    { icon: Clock3, text: "Réponse sous 48h ouvrées" },
                    { icon: Gift, text: "Devis gratuit, sans engagement" },
                    { icon: ShieldCheck, text: "Données sécurisées, RGPD" },
                  ].map((item) => (
                    <li key={item.text} className="flex items-center gap-3 text-sm font-medium">
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[hsla(245,85%,65%,0.12)] text-primary">
                        <item.icon className="h-4 w-4" />
                      </span>
                      {item.text}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm">
                  <div className="flex flex-shrink-0 -space-x-3">
                    {[
                      { src: "/images/team/cindy.jpg", alt: "Cindy" },
                      { src: "/images/team/thibault.jpg", alt: "Thibault" },
                    ].map((m) => (
                      <img
                        key={m.alt}
                        src={m.src}
                        alt={m.alt}
                        className="h-11 w-11 rounded-full border-2 border-card object-cover"
                      />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Vous échangez directement avec <strong className="text-foreground">Cindy et Thibault</strong>, pas avec un bot.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* Colonne formulaire */}
            <div className="lg:col-span-3">
          {status === "success" ? (
            <Card className="border-[hsla(158,85%,45%,0.3)] bg-[hsla(158,85%,45%,0.08)]">
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-success">
                  Demande envoyée !
                </h3>
                <p className="text-muted-foreground">
                  On revient vers vous sous 48h ouvrées.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="glass-effect shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Prénom et Nom *</label>
                      <Input
                        required
                        placeholder="Marie Dupont"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Email professionnel *</label>
                      <Input
                        required
                        type="email"
                        placeholder="marie@marque.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Entreprise *</label>
                      <Input
                        required
                        placeholder="Ma Marque SAS"
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Téléphone (optionnel)</label>
                      <Input
                        type="tel"
                        placeholder="+33 6 12 34 56 78"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Budget estimé *</label>
                      <select
                        required
                        className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        value={form.budget}
                        onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      >
                        <option value="">Sélectionner un budget</option>
                        <option value="< 2 000€">Moins de 2 000€</option>
                        <option value="2 000 – 5 000€">2 000 – 5 000€</option>
                        <option value="5 000 – 10 000€">5 000 – 10 000€</option>
                        <option value="10 000 – 20 000€">10 000 – 20 000€</option>
                        <option value="> 20 000€">Plus de 20 000€</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Secteur d&apos;activité *</label>
                      <select
                        required
                        className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        value={form.sector}
                        onChange={(e) => setForm({ ...form, sector: e.target.value })}
                      >
                        <option value="">Votre secteur</option>
                        <option value="E-commerce / Produit physique">E-commerce / Produit physique</option>
                        <option value="Mode / Beauté / Lifestyle">Mode / Beauté / Lifestyle</option>
                        <option value="Services / SaaS">Services / SaaS</option>
                        <option value="Formation / EdTech">Formation / EdTech</option>
                        <option value="Santé / Bien-être">Santé / Bien-être</option>
                        <option value="Alimentaire / Nutrition">Alimentaire / Nutrition</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Quand souhaitez-vous démarrer ?</label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      value={form.timing}
                      onChange={(e) => setForm({ ...form, timing: e.target.value })}
                    >
                      <option value="">Sélectionner un délai</option>
                      <option value="Dans la semaine">Dans la semaine</option>
                      <option value="Dans le mois">Dans le mois</option>
                      <option value="Dans 1–3 mois">Dans 1–3 mois</option>
                      <option value="Pas encore défini">Pas encore défini</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Message / Brief projet *</label>
                    <Textarea
                      required
                      rows={4}
                      placeholder="Décrivez votre marque, votre cible, vos objectifs et la plateforme visée (Meta, TikTok…)"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="rgpd"
                      required
                      checked={form.rgpd}
                      onChange={(e) => setForm({ ...form, rgpd: e.target.checked })}
                      className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <label htmlFor="rgpd" className="text-sm text-muted-foreground leading-relaxed">
                      J&apos;accepte que mes données soient utilisées pour traiter ma demande. *
                    </label>
                  </div>

                  {status === "error" && (
                    <p className="text-sm text-destructive">
                      Une erreur est survenue. Veuillez réessayer ou nous contacter directement.
                    </p>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full btn-primary-gradient"
                    disabled={status === "loading" || !form.rgpd}
                  >
                    {status === "loading" ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                        Envoi en cours…
                      </>
                    ) : (
                      <>
                        Recevoir ma proposition gratuite
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>

                </form>
              </CardContent>
            </Card>
          )}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Footer                                                               */}
      {/* ------------------------------------------------------------------ */}
      <footer className="relative overflow-hidden border-t border-border bg-background px-4 py-12">
        <div className="relative mx-auto flex max-w-5xl flex-col items-center gap-6 text-center">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-secondary">
              <Play className="ml-0.5 h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold text-foreground">
              UGC Cookie <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Agence</span>
            </span>
          </div>
          <p className="max-w-md text-sm text-muted-foreground">
            Des vidéos UGC qui font scroller, cliquer et acheter. De la stratégie au contenu livré, prêt à performer.
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <button onClick={() => scrollTo(creatorsRef)} className="text-muted-foreground transition-colors hover:text-primary">Nos créateurs</button>
            <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="text-muted-foreground transition-colors hover:text-primary">Nos offres</button>
            <button onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="text-muted-foreground transition-colors hover:text-primary">FAQ</button>
            <button onClick={() => scrollTo(contactRef)} className="text-muted-foreground transition-colors hover:text-primary">Contact</button>
          </nav>
          <p className="text-xs text-muted-foreground/60">© {new Date().getFullYear()} UGC Cookie Agence · Tous droits réservés</p>
        </div>
      </footer>

      {/* Creator modal */}
      {selectedCreator && (
        <CreatorModal
          creator={selectedCreator}
          onClose={() => setSelectedCreator(null)}
          onRequestContact={() => {
            setSelectedCreator(null);
            scrollTo(contactRef);
          }}
        />
      )}
    </div>
  );
}
